import { useMemo } from 'react';
import { Loader2, GitBranch } from 'lucide-react';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import { table, cn } from '../../../components/ui/uiTheme';
import { SectionSubtitle } from '../../../components/ui/PageLayout';
import { getRepoDeploymentBranches } from '../../../utils/deploymentBranches';
import { commitGitHubUrl, commitAuthorDisplay, formatRepoTabDate } from '../repositoryDetailUtils';

const MERGE_SUBJECT_RE = /^Merge (pull request|branch)\b/i;

function firstLine(message) {
  return (message || '').split('\n')[0] || '(no message)';
}

function normalizeSha(sha: unknown) {
  return typeof sha === 'string' ? sha.trim().toLowerCase() : '';
}

function shortSha(sha: string) {
  return sha.slice(0, 7);
}

export default function RepositoryHistoryTab({ repoData, historyCommits, historyLoading }: any) {
  const deploymentBranch = useMemo(() => getRepoDeploymentBranches(repoData)[0], [repoData]);

  // API returns newest-first — keep that order so the latest commit is at the top.
  // Dedupe by sha (and prefer stable DB id as React key) so list identity stays unique.
  const orderedCommits = useMemo(() => {
    const seen = new Set();
    return (historyCommits || []).filter((c: any) => {
      const sha = normalizeSha(c?.sha);
      if (!sha || seen.has(sha)) return false;
      seen.add(sha);
      return true;
    });
  }, [historyCommits]);

  return (
    <div role="tabpanel" id="repo-tabpanel-history" aria-labelledby="repo-tab-history">
      <FeatureGate flag="ui.repositoryDetail.historyTab">
        <div className={cn(table.wrapper, 'overflow-hidden')}>
          <div className="px-4 py-3 border-b border-dark-border flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <div>
              <SectionSubtitle>History</SectionSubtitle>
              <p className="text-sm text-slate-400 mt-0.5">
                Commit history for the deployment branch. Purple dots are merge commits.
              </p>
            </div>
            <div className="inline-flex items-center gap-1.5 text-xs text-slate-300 shrink-0">
              <GitBranch className="h-3.5 w-3.5 text-slate-500" aria-hidden />
              <span className="font-mono">{deploymentBranch}</span>
            </div>
          </div>

          {historyLoading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              <span>Loading commit history…</span>
            </div>
          ) : orderedCommits.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400">
              No commits found on <span className="font-mono">{deploymentBranch}</span>. Sync the
              repository to fetch commit history from GitHub.
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-auto p-4">
              <ol className="relative m-0 list-none p-0">
                {orderedCommits.map((c: any, index: number) => {
                  const sha = normalizeSha(c.sha) || `row-${index}`;
                  const subject = firstLine(c.message);
                  const isMerge = MERGE_SUBJECT_RE.test(subject);
                  const url = commitGitHubUrl(c);
                  const rowKey = c.id ? String(c.id) : `${sha}-${index}`;
                  const openCommit = () => {
                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                  };

                  return (
                    <li key={rowKey} className="relative flex gap-3 pb-5 last:pb-0">
                      {index < orderedCommits.length - 1 ? (
                        <span
                          className="absolute left-[7px] top-4 bottom-0 w-0.5 bg-teal-400/80"
                          aria-hidden
                        />
                      ) : null}
                      <button
                        type="button"
                        onClick={openCommit}
                        disabled={!url}
                        aria-label={url ? `Open commit ${shortSha(sha)} on GitHub` : undefined}
                        className={cn(
                          'relative z-[1] mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full border-0 p-0',
                          isMerge ? 'bg-violet-400' : 'bg-teal-400',
                          url ? 'cursor-pointer' : 'cursor-default'
                        )}
                      />
                      <div className="min-w-0 flex-1 pt-0.5">
                        <button
                          type="button"
                          onClick={openCommit}
                          disabled={!url}
                          className={cn(
                            'block w-full text-left text-sm leading-snug',
                            isMerge ? 'text-slate-200' : 'text-slate-300',
                            url ? 'hover:text-white cursor-pointer' : 'cursor-default'
                          )}
                        >
                          {subject}
                        </button>
                        <p className="mt-0.5 text-xs text-slate-500 font-mono">
                          <span>{shortSha(sha)}</span>
                          <span className="text-slate-600"> · </span>
                          <span className="font-sans text-slate-400">
                            {commitAuthorDisplay(c)} · {formatRepoTabDate(c.authorDate)}
                          </span>
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>
      </FeatureGate>
    </div>
  );
}
